import React, { useState, useEffect, useCallback } from 'react'
import { Plus, Edit, Trash2, Loader2, X, Euro, Shield, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

const TYPE_FRANCHISE_LABELS = {
  fixe: 'Montant fixe',
  pourcentage: 'Pourcentage',
  plancher: 'Plancher',
  plafond: 'Plafond',
}

const CONVENTION_LABELS = {
  irsa: 'IRSA',
  irca: 'IRCA',
  hors_convention: 'Hors convention',
}

const fmt = (v) => {
  if (v == null) return '--'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

export default function PoliceFranchisesPanel({ police, onClose }) {
  const [franchises, setFranchises] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    type_garantie: 'tous_types',
    type_sinistre: '',
    convention: '',
    montant_franchise: '',
    type_franchise: 'fixe',
    conditions: '',
    priorite: 0,
  })

  const fetchFranchises = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/polices/${police.id}/franchises`)
      const d = await r.json()
      setFranchises(d.franchises || [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [police.id])

  useEffect(() => { fetchFranchises() }, [fetchFranchises])

  const resetForm = () => {
    setForm({ type_garantie: 'tous_types', type_sinistre: '', convention: '', montant_franchise: '', type_franchise: 'fixe', conditions: '', priorite: 0 })
    setEditing(null)
    setShowForm(false)
  }

  const startEdit = (f) => {
    setEditing(f)
    setForm({
      type_garantie: f.type_garantie || 'tous_types',
      type_sinistre: f.type_sinistre || '',
      convention: f.convention || '',
      montant_franchise: f.montant_franchise || '',
      type_franchise: f.type_franchise || 'fixe',
      conditions: f.conditions || '',
      priorite: f.priorite ?? 0,
    })
    setShowForm(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.montant_franchise) { toast.error('Montant requis'); return }
    setSaving(true)
    try {
      const payload = {
        ...form,
        montant_franchise: parseFloat(form.montant_franchise),
        priorite: parseInt(form.priorite) || 0,
        convention: form.convention || null,
        type_sinistre: form.type_sinistre || null,
      }
      const url = editing
        ? `${API}/api/franchises/${editing.id}`
        : `${API}/api/polices/${police.id}/franchises`
      const method = editing ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || 'Erreur')
      }
      toast.success(editing ? 'Franchise mise à jour' : 'Franchise ajoutée')
      resetForm()
      fetchFranchises()
    } catch (e) { toast.error(e.message) }
    finally { setSaving(false) }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette franchise ?')) return
    try {
      const r = await fetch(`${API}/api/franchises/${id}`, { method: 'DELETE' })
      if (r.ok) { toast.success('Franchise supprimée'); fetchFranchises() }
    } catch (e) { toast.error(e.message) }
  }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" data-testid="franchises-panel-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" data-testid="franchises-panel">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h3 className="text-base font-bold text-slate-800">Franchises</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Police {police.numero_police} — {police.libelle || police.type_police}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Priorité basse = examinée en premier
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { resetForm(); setShowForm(true) }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="add-franchise-btn"
            >
              <Plus size={14} /> Ajouter
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
              <X size={18} className="text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
            </div>
          ) : franchises.length === 0 && !showForm ? (
            <div className="text-center py-12" data-testid="no-franchises">
              <Euro className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Aucune franchise définie</p>
              <p className="text-xs text-slate-400 mt-1">Ajoutez une franchise pour cette police.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {franchises.map(f => (
                <div key={f.id} className="bg-slate-50 rounded-lg border border-slate-200 p-3" data-testid={`franchise-row-${f.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Euro size={14} className="text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">{fmt(f.montant_franchise)}</span>
                          <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                            {TYPE_FRANCHISE_LABELS[f.type_franchise] || f.type_franchise}
                          </span>
                          {f.convention && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                              {CONVENTION_LABELS[f.convention] || f.convention}
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400">prio: {f.priorite}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {f.type_garantie === 'tous_types' ? 'Tous types' : f.type_garantie}
                          {f.type_sinistre && ` / ${f.type_sinistre}`}
                          {f.conditions && ` — ${f.conditions}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(f)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" data-testid={`edit-franchise-${f.id}`}>
                        <Edit size={14} />
                      </button>
                      <button onClick={() => handleDelete(f.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-franchise-${f.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Form */}
          {showForm && (
            <div className="mt-4 bg-white border border-indigo-200 rounded-xl p-4" data-testid="franchise-form">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">
                {editing ? 'Modifier la franchise' : 'Nouvelle franchise'}
              </h4>
              <form onSubmit={handleSave} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Montant *</label>
                    <input
                      type="number" step="0.01" required
                      value={form.montant_franchise}
                      onChange={e => setForm({ ...form, montant_franchise: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      placeholder="1500"
                      data-testid="franchise-montant-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                    <select
                      value={form.type_franchise}
                      onChange={e => setForm({ ...form, type_franchise: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      data-testid="franchise-type-select"
                    >
                      {Object.entries(TYPE_FRANCHISE_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Convention</label>
                    <select
                      value={form.convention}
                      onChange={e => setForm({ ...form, convention: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      data-testid="franchise-convention-select"
                    >
                      <option value="">Aucune</option>
                      {Object.entries(CONVENTION_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Garantie</label>
                    <input
                      type="text"
                      value={form.type_garantie}
                      onChange={e => setForm({ ...form, type_garantie: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      placeholder="tous_types"
                      data-testid="franchise-garantie-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Type sinistre (filtre)</label>
                    <select
                      value={form.type_sinistre}
                      onChange={e => setForm({ ...form, type_sinistre: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="">Tous</option>
                      <option value="flotte">Flotte</option>
                      <option value="dab">DAB</option>
                      <option value="rc_tiers">RC Tiers</option>
                      <option value="amiable">Amiable</option>
                      <option value="contentieux">Contentieux</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Priorité</label>
                    <input
                      type="number"
                      value={form.priorite}
                      onChange={e => setForm({ ...form, priorite: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      placeholder="0"
                      data-testid="franchise-priorite-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Conditions</label>
                  <input
                    type="text"
                    value={form.conditions}
                    onChange={e => setForm({ ...form, conditions: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Description libre..."
                    data-testid="franchise-conditions-input"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={resetForm} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg">
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    data-testid="franchise-save-btn"
                  >
                    {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                    {editing ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
